import type { MockContext } from '@mocks/mockFetch';

type FlowMetadata = {
  effectiveStartTime: string;
  effectiveEndTime: string;
  timeSeriesResolution: '1m' | '2m' | '5m' | '10m';
  samplingState: 'unsampled' | 'sampled' | 'mixed' | 'unknown';
  requestId: string;
};

type SamplingState = FlowMetadata['samplingState'];

type MockFlowReport = {
  id: string;
  orgId: string;
  status: 'completed';
  format: 'pdf';
  startTime: string;
  endTime: string;
  visibleStartTime: string;
  visibleEndTime: string;
  siteId?: string;
  fileName: string;
  mimeType: 'application/pdf';
  sizeBytes: number;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  expiresAt: string;
};

type MockFlowRecord = {
  schemaVersion: string;
  recordId: string;
  orgId: string;
  flowType: string;
  samplerAddress: string;
  observationDomainId: number;
  flowSequence: number;
  receivedAt: string;
  flowStartTime: string;
  flowEndTime: string;
  observedDelayNs: number;
  sourceAddress: string;
  destinationAddress: string;
  sourcePort?: number;
  destinationPort?: number;
  protocol?: string;
  bytes: number;
  packets: number;
  inIfIndex: number;
  outIfIndex: number;
  nextHop: string;
  sourceAs: number;
  destinationAs: number;
  nextHopAs: number;
  vlanId: number;
  sourceVlan: number;
  destinationVlan: number;
  sourceNetwork: string;
  destinationNetwork: string;
  tcpFlags: number;
  icmpType: number;
  icmpCode: number;
  forwardingStatus: number;
  ipTos: number;
  ipTtl: number;
  etherType: string;
  samplingState: SamplingState;
  samplingRate: number;
};

type RawFlowDocument = {
  raw_document_id: string;
  sampler_address: string;
  flow_sequence: number;
  received_at: string;
  flow_start_time: string;
  flow_end_time: string;
  source_ip: string;
  destination_ip: string;
  raw_bytes: number;
  raw_packets: number;
  input_if_index: number;
  output_if_index: number;
  vlan: number;
  flow_type: string;
  observation_domain_id: number;
  source_port?: number;
  destination_port?: number;
  ip_protocol?: number;
  next_hop?: string;
  sampling_state?: SamplingState;
  sampling_rate?: number;
};

type FlowMetric = { key: string; bytes: number; packets: number; recordCount: number; observedBytesPerSecond: number; trafficSharePercent: number };
type FlowTimeBucket = {
  startTime: string;
  endTime: string;
  bucketDurationSeconds: number;
  bytes: number;
  packets: number;
  observedBytesPerSecond: number;
  observedPacketsPerSecond: number;
  recordCount: number;
};
type FlowExporterIdentity = {
  flowType: string;
  samplerAddress: string;
  observationDomainId: number;
};
type FlowConversation = {
  exporter: FlowExporterIdentity;
  mode: 'directional' | 'bidirectional' | 'endpoint_pair';
  sourceAddress: string;
  destinationAddress: string;
  sourcePort?: number;
  destinationPort?: number;
  protocol?: string;
  bytes: number;
  packets: number;
  directionalTraffic?: {
    firstToSecondBytes: number;
    firstToSecondPackets: number;
    secondToFirstBytes: number;
    secondToFirstPackets: number;
  };
  directionalPaths?: Array<{
    direction: 'first_to_second' | 'second_to_first';
    ingressIfIndex?: number;
    egressIfIndex?: number;
    ingressInterfaceName?: string;
    egressInterfaceName?: string;
    bytes: number;
    packets: number;
    recordCount: number;
  }>;
  directionalPathsTruncated?: boolean;
  recordCount: number;
  trafficSharePercent: number;
  timeSeries: FlowTimeBucket[];
};
type FlowEndpoint = {
  address: string;
  sentBytes: number;
  receivedBytes: number;
  sentPackets: number;
  receivedPackets: number;
  ingressBytes: number;
  egressBytes: number;
  ingressPackets: number;
  egressPackets: number;
  bytes: number;
  packets: number;
  trafficSharePercent: number;
  timeSeries: FlowTimeBucket[];
  recordCount: number;
};
type FlowService = {
  label: string;
  protocol: string;
  servicePort?: number;
  ianaServiceName?: string;
  toServiceBytes: number;
  fromServiceBytes: number;
  toServicePackets: number;
  fromServicePackets: number;
  bytes: number;
  packets: number;
  trafficSharePercent: number;
  timeSeries: FlowTimeBucket[];
  recordCount: number;
};
type FlowInterfaceTotals = {
  exporter: FlowExporterIdentity;
  ifIndex: number;
  interfaceName?: string;
  ingressBytes: number;
  egressBytes: number;
  totalBytes: number;
  ingressPackets: number;
  egressPackets: number;
  totalPackets: number;
  ingressRecordCount: number;
  egressRecordCount: number;
};
type FlowInterfaceTraffic = {
  exporter: FlowExporterIdentity;
  direction: 'input' | 'output';
  ifIndex: number;
  interfaceName?: string;
  bytes: number;
  packets: number;
  recordCount: number;
};
type FlowSourceSummary = {
  sourceKey: string;
  flowType: string;
  samplerAddress: string;
  observationDomainId: number;
  lastSeenAt: string;
  samplingState: SamplingState;
  bytes: number;
  packets: number;
  recordCount: number;
  observedBytesPerSecond: number;
  observedPacketsPerSecond: number;
  candidateSequenceGap?: number;
  sequenceResetCandidate: boolean;
};

const mockInterfaceName = (ifIndex: number): string | undefined => ifIndex === 0 ? undefined : 'mock-if-' + ifIndex;

const flowOrgID = 'c19ae4a2-732a-4cf7-8956-3e6f720ffc7f';

// Normalized from 20 real edge_flowlogs documents supplied for UI
// verification: 13 IPFIX records and seven sampled sFlow records.
// Control-plane mapping fields remain absent because the documents do not
// provide trusted connector, device, site, or interface names.
const rawFlowDocuments: RawFlowDocument[] = [
  {
    raw_document_id: 'J4fMs6ABSIWH5X9yW_1D',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 757,
    received_at: '2026-09-18T09:14:59.660Z',
    flow_start_time: '2026-09-18T09:14:48.881Z',
    flow_end_time: '2026-09-18T09:14:48.881Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    vlan: 20
  },
  {
    raw_document_id: 'TYfLs6ABSIWH5X9y5_Ko',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 731,
    received_at: '2026-09-18T09:14:29.660Z',
    flow_start_time: '2026-09-18T09:14:18.516Z',
    flow_end_time: '2026-09-18T09:14:18.516Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    vlan: 20
  },
  {
    raw_document_id: 'Q4fLs6ABSIWH5X9ycud0',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 705,
    received_at: '2026-09-18T09:13:59.660Z',
    flow_start_time: '2026-09-18T09:13:48.449Z',
    flow_end_time: '2026-09-18T09:13:48.449Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    vlan: 20
  },
  {
    raw_document_id: 'i4fJs6ABSIWH5X9yCrEi',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 579,
    received_at: '2026-09-18T09:11:21.660Z',
    flow_start_time: '2026-09-18T09:11:10.873Z',
    flow_end_time: '2026-09-18T09:11:10.873Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1,
    output_if_index: 1000049,
    vlan: 20
  },
  {
    raw_document_id: 'RIfHs6ABSIWH5X9y2Jj7',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 503,
    received_at: '2026-09-18T09:10:03.660Z',
    flow_start_time: '2026-09-18T09:09:45.815Z',
    flow_end_time: '2026-09-18T09:09:52.071Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 168,
    raw_packets: 2,
    input_if_index: 1,
    output_if_index: 1000049,
    vlan: 20
  },
  {
    raw_document_id: 'K4fCs6ABSIWH5X9yZhta',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.8',
    flow_sequence: 201,
    received_at: '2026-09-18T09:04:06.660Z',
    flow_start_time: '2026-09-18T09:03:55.715Z',
    flow_end_time: '2026-09-18T09:03:55.715Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    vlan: 20
  },
  {
    raw_document_id: 'vIjMs6ABSIWH5X9yuQRG',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 22636,
    received_at: '2026-09-18T09:15:23.260Z',
    flow_start_time: '2026-09-18T09:15:04.580Z',
    flow_end_time: '2026-09-18T09:15:04.580Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000012,
    output_if_index: 11,
    vlan: 120
  },
  {
    raw_document_id: 'JofMs6ABSIWH5X9yW_1D',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 22595,
    received_at: '2026-09-18T09:14:59.261Z',
    flow_start_time: '2026-09-18T09:14:40.396Z',
    flow_end_time: '2026-09-18T09:14:40.396Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000012,
    output_if_index: 11,
    vlan: 120
  },
  {
    raw_document_id: 'fYfJs6ABSIWH5X9yx8Fo',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 22394,
    received_at: '2026-09-18T09:12:10.260Z',
    flow_start_time: '2026-09-18T09:11:51.609Z',
    flow_end_time: '2026-09-18T09:11:51.609Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 1000012,
    output_if_index: 11,
    vlan: 120
  },
  {
    raw_document_id: 'RYfHs6ABSIWH5X9ykZIA',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 22193,
    received_at: '2026-09-18T09:09:52.261Z',
    flow_start_time: '2026-09-18T09:09:32.772Z',
    flow_end_time: '2026-09-18T09:09:32.772Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 11,
    output_if_index: 1000012,
    vlan: 531
  },
  {
    raw_document_id: 'qIfGs6ABSIWH5X9yvX36',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 22111,
    received_at: '2026-09-18T09:08:51.261Z',
    flow_start_time: '2026-09-18T09:08:31.713Z',
    flow_end_time: '2026-09-18T09:08:31.713Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 11,
    output_if_index: 1000012,
    vlan: 531
  },
  {
    raw_document_id: 'TIfCs6ABSIWH5X9yqx-T',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 21748,
    received_at: '2026-09-18T09:04:24.260Z',
    flow_start_time: '2026-09-18T09:04:05.633Z',
    flow_end_time: '2026-09-18T09:04:05.633Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 11,
    output_if_index: 1000012,
    vlan: 531
  },
  {
    raw_document_id: 'FYfCs6ABSIWH5X9y3SZt',
    flow_type: 'IPFIX',
    observation_domain_id: 1,
    sampler_address: '10.20.0.6',
    flow_sequence: 21789,
    received_at: '2026-09-18T09:04:37.260Z',
    flow_start_time: '2026-09-18T09:04:18.497Z',
    flow_end_time: '2026-09-18T09:04:18.497Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 84,
    raw_packets: 1,
    input_if_index: 11,
    output_if_index: 1000011,
    vlan: 530
  },
  {
    raw_document_id: 'Gsnos6ABRu-ikBW3QKoS',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437176,
    received_at: '2026-09-18T09:45:33.261Z',
    flow_start_time: '2026-09-18T09:45:33.173Z',
    flow_end_time: '2026-09-18T09:45:33.173Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 106,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    next_hop: '',
    vlan: 120,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: 'Gcnos6ABRu-ikBW3QKoS',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437172,
    received_at: '2026-09-18T09:45:27.260Z',
    flow_start_time: '2026-09-18T09:45:27.165Z',
    flow_end_time: '2026-09-18T09:45:27.165Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 102,
    raw_packets: 1,
    input_if_index: 1,
    output_if_index: 1000049,
    next_hop: '',
    vlan: 0,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: 'Nsnns6ABRu-ikBW3-aPv',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437160,
    received_at: '2026-09-18T09:45:09.260Z',
    flow_start_time: '2026-09-18T09:45:09.140Z',
    flow_end_time: '2026-09-18T09:45:09.140Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 106,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    next_hop: '',
    vlan: 120,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: '1Mnms6ABRu-ikBW3d4Eo',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437102,
    received_at: '2026-09-18T09:43:35.062Z',
    flow_start_time: '2026-09-18T09:43:35.014Z',
    flow_end_time: '2026-09-18T09:43:35.014Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 123,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 0,
    next_hop: '',
    vlan: 0,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: 'Hsnls6ABRu-ikBW3fG1E',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437061,
    received_at: '2026-09-18T09:42:26.061Z',
    flow_start_time: '2026-09-18T09:42:25.930Z',
    flow_end_time: '2026-09-18T09:42:25.930Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 106,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    next_hop: '',
    vlan: 120,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: 'sMnks6ABRu-ikBW3HFAZ',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 437006,
    received_at: '2026-09-18T09:40:55.860Z',
    flow_start_time: '2026-09-18T09:40:55.804Z',
    flow_end_time: '2026-09-18T09:40:55.804Z',
    source_ip: '10.110.20.101',
    destination_ip: '10.110.120.101',
    raw_bytes: 106,
    raw_packets: 1,
    input_if_index: 1000049,
    output_if_index: 1,
    next_hop: '',
    vlan: 120,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  },
  {
    raw_document_id: 'v8nis6ABRu-ikBW3biun',
    flow_type: 'SFLOW_5',
    sampler_address: '10.20.0.9',
    observation_domain_id: 0,
    flow_sequence: 436939,
    received_at: '2026-09-18T09:39:05.861Z',
    flow_start_time: '2026-09-18T09:39:05.663Z',
    flow_end_time: '2026-09-18T09:39:05.663Z',
    source_ip: '10.110.120.101',
    destination_ip: '10.110.20.101',
    raw_bytes: 102,
    raw_packets: 1,
    input_if_index: 1,
    output_if_index: 1000049,
    next_hop: '',
    vlan: 0,
    ip_protocol: 1,
    sampling_state: 'sampled',
    sampling_rate: 512
  }
];

// Keep the captured records realistic while anchoring the fixture to the
// active browser session. Fixed capture timestamps would otherwise make every
// rolling-window query empty.
const latestFixtureEventTime = Math.max(...rawFlowDocuments.map((document) => Date.parse(document.flow_end_time)));
const fixtureTimeOffsetMs = Date.now() - latestFixtureEventTime - 5 * 60_000;
const currentFixtureTime = (timestamp: string): string => new Date(Date.parse(timestamp) + fixtureTimeOffsetMs).toISOString();
const defaultMockWindowEnd = new Date().toISOString();
const defaultMockWindowStart = new Date(Date.now() - 60 * 60_000).toISOString();

const protocolName = (value: number | undefined): string | undefined => {
  if (value === 6) return 'TCP';
  if (value === 17) return 'UDP';
  if (value === 1) return 'ICMP';
  return value == null ? undefined : String(value);
};

const ipv4Network = (address: string): string => {
  const octets = address.split('.');
  return octets.length === 4 ? octets.slice(0, 3).join('.') + '.0/24' : '';
};

const observedDelayNs = (receivedAt: string, flowEndTime: string): number =>
  Math.max(0, Date.parse(receivedAt) - Date.parse(flowEndTime)) * 1_000_000;

const capturedRecords: MockFlowRecord[] = rawFlowDocuments
  .map((document) => {
    const flowType = document.flow_type;
    const sourceAddress = document.source_ip;
    const destinationAddress = document.destination_ip;
    return {
      schemaVersion: 'flow_record.v1',
      recordId: 'mock-' + document.raw_document_id,
      orgId: flowOrgID,
      flowType,
      samplerAddress: document.sampler_address,
      observationDomainId: document.observation_domain_id,
      flowSequence: document.flow_sequence,
      receivedAt: currentFixtureTime(document.received_at),
      flowStartTime: currentFixtureTime(document.flow_start_time),
      flowEndTime: currentFixtureTime(document.flow_end_time),
      observedDelayNs: observedDelayNs(document.received_at, document.flow_end_time),
      sourceAddress,
      destinationAddress,
      sourcePort: document.source_port,
      destinationPort: document.destination_port,
      protocol: protocolName(document.ip_protocol ?? (flowType === 'SFLOW_5' ? undefined : 1)),
      bytes: document.raw_bytes,
      packets: document.raw_packets,
      inIfIndex: document.input_if_index,
      outIfIndex: document.output_if_index,
      nextHop: document.next_hop ?? '0.0.0.0',
      sourceAs: 0,
      destinationAs: 0,
      nextHopAs: 0,
      vlanId: document.vlan,
      sourceVlan: document.vlan,
      destinationVlan: document.vlan,
      tcpFlags: 0,
      icmpType: 0,
      icmpCode: 0,
      forwardingStatus: 0,
      ipTos: 0,
      ipTtl: 64,
      etherType: 'IPv4',
      // GoFlow reports zero for Arista IPFIX sampling metadata today. The
      // public contract represents that value as unknown, not unsampled.
      samplingState: document.sampling_state ?? 'unknown',
      samplingRate: document.sampling_rate ?? 0,
      sourceNetwork: ipv4Network(sourceAddress),
      destinationNetwork: ipv4Network(destinationAddress)
    };
  })
  .sort((left, right) => right.receivedAt.localeCompare(left.receivedAt));

type RepresentativeFlowProfile = {
  flowType: string;
  samplerAddress: string;
  observationDomainId: number;
  sourceAddress: string;
  destinationAddress: string;
  sourcePort?: number;
  destinationPort?: number;
  protocol: string;
  bytes: number;
  packets: number;
  inIfIndex: number;
  outIfIndex: number;
  nextHop?: string;
  sourceAs?: number;
  destinationAs?: number;
  vlanId: number;
  samplingState: SamplingState;
  samplingRate: number;
};

// A representative, bounded three-hour fixture calibrated from the current
// edge-flowlogs distribution. It preserves the real exporter identities and
// private addressing while providing enough traffic diversity for the dashboard
// to demonstrate a useful trend, protocol mix, ports, VLANs, and talkers.
// These are illustrative observations, not a claim that every captured record
// has been reproduced in the browser mock.
const representativeFlowProfiles: RepresentativeFlowProfile[] = [
  {
    flowType: 'SFLOW_5',
    samplerAddress: '10.20.0.9',
    observationDomainId: 0,
    sourceAddress: '10.110.20.101',
    destinationAddress: '10.110.120.101',
    sourcePort: 5003,
    destinationPort: 6003,
    protocol: 'UDP',
    bytes: 335_000,
    packets: 287,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: 'Unknown',
    vlanId: 120,
    samplingState: 'sampled',
    samplingRate: 512
  },
  {
    flowType: 'SFLOW_5',
    samplerAddress: '10.20.0.9',
    observationDomainId: 0,
    sourceAddress: '10.110.20.101',
    destinationAddress: '10.110.120.101',
    sourcePort: 5004,
    destinationPort: 6004,
    protocol: 'UDP',
    bytes: 185_000,
    packets: 158,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: '0.0.0.0',
    vlanId: 120,
    samplingState: 'sampled',
    samplingRate: 512
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.6',
    observationDomainId: 1,
    sourceAddress: '10.151.11.13',
    destinationAddress: '10.151.12.12',
    sourcePort: 5002,
    destinationPort: 5432,
    protocol: 'TCP',
    bytes: 180_000,
    packets: 152,
    inIfIndex: 1_000_012,
    outIfIndex: 11,
    nextHop: '10.255.30.2',
    sourceAs: 64_512,
    destinationAs: 64_513,
    vlanId: 1511,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.6',
    observationDomainId: 1,
    sourceAddress: '10.151.11.13',
    destinationAddress: '10.151.12.12',
    sourcePort: 5001,
    destinationPort: 443,
    protocol: 'TCP',
    bytes: 80_000,
    packets: 68,
    inIfIndex: 1_000_012,
    outIfIndex: 11,
    nextHop: '10.110.120.101',
    sourceAs: 64_512,
    destinationAs: 15_169,
    vlanId: 1511,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.110.120.101',
    destinationAddress: '10.151.12.11',
    sourcePort: 5004,
    destinationPort: 6002,
    protocol: 'UDP',
    bytes: 155_000,
    packets: 139,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: '10.110.20.101',
    sourceAs: 64_510,
    destinationAs: 64_512,
    vlanId: 20,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.110.120.101',
    destinationAddress: '10.151.12.11',
    sourcePort: 5003,
    destinationPort: 6001,
    protocol: 'UDP',
    bytes: 90_000,
    packets: 81,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: '10.255.31.2',
    sourceAs: 64_510,
    destinationAs: 64_512,
    vlanId: 20,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.6',
    observationDomainId: 1,
    sourceAddress: '10.110.20.2',
    destinationAddress: '10.151.12.12',
    sourcePort: 5001,
    destinationPort: 53,
    protocol: 'TCP',
    bytes: 50_000,
    packets: 47,
    inIfIndex: 11,
    outIfIndex: 1_000_012,
    nextHop: '10.255.30.2',
    sourceAs: 64_510,
    destinationAs: 64_513,
    vlanId: 1512,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.6',
    observationDomainId: 1,
    sourceAddress: '10.110.20.2',
    destinationAddress: '10.151.12.12',
    sourcePort: 5004,
    destinationPort: 6002,
    protocol: 'TCP',
    bytes: 35_000,
    packets: 33,
    inIfIndex: 11,
    outIfIndex: 1_000_012,
    nextHop: 'Unknown',
    vlanId: 1512,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.151.12.12',
    destinationAddress: '10.151.11.13',
    protocol: 'ICMP',
    bytes: 4_000,
    packets: 6,
    inIfIndex: 1,
    outIfIndex: 1_000_049,
    nextHop: '0.0.0.0',
    vlanId: 0,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.151.12.11',
    destinationAddress: '10.151.11.13',
    protocol: 'ICMP',
    bytes: 1_700,
    packets: 2,
    inIfIndex: 1,
    outIfIndex: 1_000_049,
    nextHop: '10.110.120.101',
    vlanId: 0,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.6',
    observationDomainId: 1,
    sourceAddress: '10.110.120.101',
    destinationAddress: '10.151.11.13',
    sourcePort: 6005,
    destinationPort: 8443,
    protocol: 'TCP',
    bytes: 28_000,
    packets: 24,
    inIfIndex: 1_000_012,
    outIfIndex: 11,
    nextHop: '10.255.30.2',
    sourceAs: 64_512,
    destinationAs: 15_169,
    vlanId: 1511,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.110.20.2',
    destinationAddress: '10.151.12.11',
    sourcePort: 5005,
    destinationPort: 8080,
    protocol: 'TCP',
    bytes: 22_000,
    packets: 19,
    inIfIndex: 1,
    outIfIndex: 1_000_049,
    nextHop: '10.110.20.101',
    sourceAs: 64_510,
    destinationAs: 64_512,
    vlanId: 20,
    samplingState: 'unknown',
    samplingRate: 0
  },
  {
    flowType: 'SFLOW_5',
    samplerAddress: '10.20.0.9',
    observationDomainId: 0,
    sourceAddress: '10.110.20.102',
    destinationAddress: '10.110.120.102',
    sourcePort: 5005,
    destinationPort: 6005,
    protocol: 'UDP',
    bytes: 16_000,
    packets: 14,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: '0.0.0.0',
    vlanId: 120,
    samplingState: 'sampled',
    samplingRate: 512
  },
  {
    flowType: 'IPFIX',
    samplerAddress: '10.20.0.8',
    observationDomainId: 1,
    sourceAddress: '10.151.11.14',
    destinationAddress: '10.151.12.12',
    sourcePort: 5006,
    destinationPort: 9443,
    protocol: 'TCP',
    bytes: 11_000,
    packets: 9,
    inIfIndex: 1_000_049,
    outIfIndex: 1,
    nextHop: 'Unknown',
    vlanId: 1512,
    samplingState: 'unknown',
    samplingRate: 0
  }
];

const representativeWindowRecords = (): MockFlowRecord[] => {
  const factors = [0.72, 0.88, 1.04, 0.91, 1.16, 0.98, 0.81, 1.09, 0.94];
  const minutesPerWindow = 10;
  const bucketCount = 18;
  // Keep the newest synthetic observation in the last completed minute. A
  // whole minute's volume in the partially completed current minute would make
  // its correctly calculated edge-bucket rate look artificially high.
  const lastCompletedMinuteStart = Math.floor(Date.now() / 60_000) * 60_000 - 60_000;

  // Split an integer total without changing it. The per-minute fixture is for
  // chart realism only: the complete ten-minute profile total must stay
  // identical so rankings and aggregate traffic remain stable.
  const distribute = (total: number, weights: number[]): number[] => {
    const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
    const raw = weights.map((weight) => total * weight / weightTotal);
    const distributed = raw.map((value) => Math.floor(value));
    const remainder = total - distributed.reduce((sum, value) => sum + value, 0);
    [...raw.keys()]
      .sort((left, right) => (raw[right]! % 1) - (raw[left]! % 1))
      .slice(0, remainder)
      .forEach((index) => { distributed[index]! += 1; });
    return distributed;
  };

  // Create repeatable minute-level variation without smoothing the series into
  // an artificial curve. The weights are normalized by distribute(), so this
  // affects only the shape of a ten-minute window, never its total traffic.
  const minuteWeights = (profileIndex: number, bucket: number): number[] => {
    const unitRandom = (minute: number): number => {
      let value = Math.imul(profileIndex + 1, 0x9e3779b1)
        ^ Math.imul(bucket + 1, 0x85ebca6b)
        ^ Math.imul(minute + 1, 0xc2b2ae35);
      value ^= value >>> 16;
      value = Math.imul(value, 0x7feb352d);
      value ^= value >>> 15;
      return (value >>> 0) / 0x1_0000_0000;
    };

    // 0.35–1.65 keeps every minute represented while still making the mock
    // resemble variable observed flow traffic rather than an interpolated
    // trend line.
    return Array.from({ length: minutesPerWindow }, (_, minute) => 0.35 + unitRandom(minute) * 1.3);
  };

  return Array.from({ length: bucketCount }, (_, bucket) =>
    representativeFlowProfiles.flatMap((profile, profileIndex) => {
      const factor = factors[(bucket + profileIndex * 2) % factors.length] ?? 1;
      const totalBytes = Math.round(profile.bytes * factor);
      const totalPackets = Math.max(1, Math.round(profile.packets * factor));
      const weights = minuteWeights(profileIndex, bucket);
      const bytesByMinute = distribute(totalBytes, weights);
      const packetsByMinute = distribute(totalPackets, weights);

      return Array.from({ length: minutesPerWindow }, (_, minute) => {
        const minutesAgo = (bucketCount - 1 - bucket) * minutesPerWindow + (minutesPerWindow - 1 - minute);
        const endAt = new Date(lastCompletedMinuteStart - minutesAgo * 60_000);
        const startAt = new Date(endAt.getTime() - 15_000);
        const receivedAt = new Date(endAt.getTime() + 500);
        const flowSequence = 900_000 + (bucket * minutesPerWindow + minute) * representativeFlowProfiles.length + profileIndex;

        return {
          schemaVersion: 'flow_record.v1',
          recordId: `mock-representative-${bucket}-${minute}-${profileIndex}`,
          orgId: flowOrgID,
          flowType: profile.flowType,
          samplerAddress: profile.samplerAddress,
          observationDomainId: profile.observationDomainId,
          flowSequence,
          receivedAt: receivedAt.toISOString(),
          flowStartTime: startAt.toISOString(),
          flowEndTime: endAt.toISOString(),
          observedDelayNs: 500_000_000,
          sourceAddress: profile.sourceAddress,
          destinationAddress: profile.destinationAddress,
          sourcePort: profile.sourcePort,
          destinationPort: profile.destinationPort,
          protocol: profile.protocol,
          bytes: bytesByMinute[minute]!,
          packets: packetsByMinute[minute]!,
          inIfIndex: profile.inIfIndex,
          outIfIndex: profile.outIfIndex,
          nextHop: profile.nextHop ?? 'Unknown',
          sourceAs: profile.sourceAs ?? 0,
          destinationAs: profile.destinationAs ?? 0,
          nextHopAs: 0,
          vlanId: profile.vlanId,
          sourceVlan: profile.vlanId,
          destinationVlan: profile.vlanId,
          sourceNetwork: ipv4Network(profile.sourceAddress),
          destinationNetwork: ipv4Network(profile.destinationAddress),
          tcpFlags: profile.protocol === 'TCP' ? 24 : 0,
          icmpType: profile.protocol === 'ICMP' ? 8 : 0,
          icmpCode: 0,
          forwardingStatus: 0,
          ipTos: 0,
          ipTtl: 64,
          etherType: 'IPv4',
          samplingState: profile.samplingState,
          samplingRate: profile.samplingRate
        };
      });
    })
  ).flat();
};

const records: MockFlowRecord[] = [...capturedRecords, ...representativeWindowRecords()].sort((left, right) =>
  right.receivedAt.localeCompare(left.receivedAt)
);

const aggregateSamplingState = (selectedRecords: MockFlowRecord[]): SamplingState => {
  const states = [...new Set(selectedRecords.map((record) => record.samplingState))];
  if (states.length === 0) return 'unknown';
  if (states.length === 1) return states[0] ?? 'unknown';
  return 'mixed';
};

// Keep mock responses at the same bounded chart resolution selected by
// flow-service. This changes only chart bucket density; ranking and total
// counters remain exact across the complete requested interval.
const timeSeriesIntervalMilliseconds = (startTime: string, endTime: string): number => {
  const duration = Date.parse(endTime) - Date.parse(startTime);
  if (duration <= 3 * 60 * 60_000) return 60_000;
  if (duration <= 6 * 60 * 60_000) return 2 * 60_000;
  if (duration <= 12 * 60 * 60_000) return 5 * 60_000;
  return 10 * 60_000;
};

const timeSeriesResolution = (intervalMilliseconds: number): FlowMetadata['timeSeriesResolution'] => {
  if (intervalMilliseconds === 60_000) return '1m';
  if (intervalMilliseconds === 2 * 60_000) return '2m';
  if (intervalMilliseconds === 5 * 60_000) return '5m';
  return '10m';
};

const mockMetadata = (url: URL, selectedRecords: MockFlowRecord[]): FlowMetadata => {
  const effectiveStartTime = url.searchParams.get('startTime') ?? defaultMockWindowStart;
  const effectiveEndTime = url.searchParams.get('endTime') ?? defaultMockWindowEnd;
  return {
    effectiveStartTime,
    effectiveEndTime,
    timeSeriesResolution: timeSeriesResolution(timeSeriesIntervalMilliseconds(effectiveStartTime, effectiveEndTime)),
    samplingState: aggregateSamplingState(selectedRecords),
    requestId: 'mock-flow-query'
  };
};

const json = (body: unknown, status = 200) =>
  new Response(JSON.stringify(body), {
    status,
    headers: {
      'Content-Type': 'application/json',
      'X-Mock-Route': 'flows'
    }
  });

const mockReports = new Map<string, MockFlowReport>();
let nextMockReportId = 1;

const orgIdFromPath = (pathname: string): string | null => {
  const match = pathname.match(/\/orgs\/([^/]+)\//);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const createMockReport = (orgId: string, payload: Record<string, unknown>): MockFlowReport | null => {
  const format = payload.format;
  const startTime = payload.startTime;
  const endTime = payload.endTime;
  const visibleStartTime = payload.visibleStartTime;
  const visibleEndTime = payload.visibleEndTime;
  if (
    format !== 'pdf'
    || typeof startTime !== 'string'
    || typeof endTime !== 'string'
    || typeof visibleStartTime !== 'string'
    || typeof visibleEndTime !== 'string'
  ) return null;
  const createdAt = new Date().toISOString();
  const report: MockFlowReport = {
    id: `00000000-0000-4000-8000-${String(nextMockReportId++).padStart(12, '0')}`,
    orgId,
    status: 'completed',
    format,
    startTime,
    endTime,
    visibleStartTime,
    visibleEndTime,
    ...(typeof payload.siteId === 'string' ? { siteId: payload.siteId } : {}),
    fileName: 'mock-flow-analytics-report.pdf',
    mimeType: 'application/pdf',
    sizeBytes: 0,
    createdAt,
    startedAt: createdAt,
    completedAt: createdAt,
    expiresAt: new Date(Date.parse(createdAt) + 30 * 60_000).toISOString()
  };
  report.sizeBytes = new TextEncoder().encode(mockPdf(report)).byteLength;
  mockReports.set(report.id, report);
  return report;
};

const pdfSafeText = (value: string): string => value
  .replace(/[\\()]/g, '\\$&')
  .replace(/[–—]/g, '-')
  .replace(/[^\x20-\x7e]/g, '?');

const pdfTextColor = (x: number, y: number, value: string, size = 10, bold = false, color = '0.08 0.12 0.16'): string =>
  `${color} rg BT /F${bold ? '2' : '1'} ${size} Tf 1 0 0 1 ${x} ${y} Tm (${pdfSafeText(value)}) Tj ET\n`;

const pdfText = (x: number, y: number, value: string, size = 10, bold = false): string =>
  pdfTextColor(x, y, value, size, bold);

const pdfTextRight = (right: number, y: number, value: string, size = 10, bold = false, color = '0.08 0.12 0.16'): string => {
  // Helvetica's average glyph width is a little over half the point size.
  // This deliberately leaves a small buffer inside each numeric column rather
  // than letting right-aligned values run beyond the page boundary.
  const estimatedWidth = pdfSafeText(value).length * size * (bold ? 0.57 : 0.52);
  return pdfTextColor(right - estimatedWidth, y, value, size, bold, color);
};

const pdfRule = (x1: number, y1: number, x2: number, y2: number, color = '0.84 0.88 0.91', width = 0.8): string =>
  `${color} RG ${width} w ${x1} ${y1} m ${x2} ${y2} l S\n`;

const pdfLineChart = (x: number, y: number, width: number, height: number, values: number[]): string => {
  const maximum = Math.max(...values, 1);
  const points = values.map((value, index) => {
    const pointX = x + index / Math.max(1, values.length - 1) * width;
    const pointY = y + value / maximum * height;
    return `${pointX.toFixed(1)} ${pointY.toFixed(1)} ${index === 0 ? 'm' : 'l'}`;
  }).join(' ');
  return [
    `0.96 0.98 0.99 rg ${x} ${y} ${width} ${height} re f\n`,
    pdfRule(x, y, x + width, y),
    pdfRule(x, y + height / 2, x + width, y + height / 2, '0.88 0.92 0.95', 0.6),
    pdfRule(x, y + height, x + width, y + height, '0.88 0.92 0.95', 0.6),
    `0.03 0.48 0.73 RG 1.8 w ${points} S\n`
  ].join('');
};

const pdfTable = (title: string, columns: string[], rows: string[][]): string => {
  const left = 58;
  const contentWidth = 496;
  const firstColumnWidth = contentWidth * 0.5;
  const remainingWidth = columns.length > 1 ? (contentWidth - firstColumnWidth) / (columns.length - 1) : 0;
  const columnX = (index: number): number => index === 0 ? left : left + firstColumnWidth + (index - 1) * remainingWidth;
  let content = pdfText(left, 754, title, 19, true);
  content += pdfText(left, 734, 'Flow Analytics development mock - representative server report content', 9);
  content += '0.95 0.97 0.98 rg 58 694 496 24 re f\n';
  columns.forEach((column, index) => {
    const x = index === 0 ? columnX(index) + 8 : columnX(index) + remainingWidth - 8;
    content += index === 0 ? pdfText(x, 703, column, 8, true) : pdfTextRight(x, 703, column, 8, true);
  });
  rows.forEach((row, rowIndex) => {
    const y = 674 - rowIndex * 45;
    content += pdfRule(left, y - 10, left + contentWidth, y - 10);
    row.forEach((value, index) => {
      const x = index === 0 ? columnX(index) + 8 : columnX(index) + remainingWidth - 8;
      content += index === 0 ? pdfText(x, y, value, 9, true) : pdfTextRight(x, y, value, 9);
    });
  });
  return content;
};

const buildMockPdf = (pages: string[]): string => {
  const objects: string[] = ['', '<< /Type /Catalog /Pages 2 0 R >>', '', '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>', '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'];
  const pageObjectIds: number[] = [];
  pages.forEach((content) => {
    const pageId = objects.length;
    const contentId = pageId + 1;
    pageObjectIds.push(pageId);
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`);
    objects.push(`<< /Length ${content.length} >>\nstream\n${content}endstream`);
  });
  objects[2] = `<< /Type /Pages /Count ${pageObjectIds.length} /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] >>`;
  let document = '%PDF-1.4\n';
  const offsets = [0];
  for (let index = 1; index < objects.length; index += 1) {
    offsets[index] = document.length;
    document += `${index} 0 obj\n${objects[index]}\nendobj\n`;
  }
  const xrefOffset = document.length;
  document += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let index = 1; index < objects.length; index += 1) document += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  document += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return document;
};

const reportColors = ['0.10 0.38 0.70', '0.20 0.65 0.86', '0.85 0.51 0.03', '0.93 0.71 0.16', '0.38 0.44 0.51', '0.47 0.25 0.88', '0.03 0.58 0.61', '0.88 0.18 0.42', '0.10 0.66 0.43', '0.93 0.45 0.13'];

type PdfTableColumn = {
  label: string;
  width: number;
  align?: 'left' | 'right' | 'center';
  swatch?: boolean;
};

type PdfChartSeries = {
  color: string;
  values: number[];
};

type PdfReportOverview = {
  totalBytes: number;
  totalPackets: number;
  observedRecordCount: number;
  metadata: FlowMetadata;
};

const reportShort = (value: string, limit: number): string => value.length > limit ? value.slice(0, Math.max(0, limit - 3)) + '...' : value;

const reportBytes = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)} MB`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 100_000 ? 0 : 1)} KB`;
  return `${Math.round(value)} B`;
};

const reportPackets = (value: number): string => value >= 1_000 ? `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K` : `${Math.round(value)}`;

const reportRate = (bytesPerSecond: number): string => {
  const bitsPerSecond = bytesPerSecond * 8;
  if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(1)} Mbps`;
  if (bitsPerSecond >= 1_000) return `${(bitsPerSecond / 1_000).toFixed(1)} Kbps`;
  return `${Math.round(bitsPerSecond)} bps`;
};

const reportPacketRate = (packetsPerSecond: number): string => packetsPerSecond >= 1_000
  ? `${(packetsPerSecond / 1_000).toFixed(1)}K pkt/s`
  : `${packetsPerSecond.toFixed(packetsPerSecond < 10 ? 1 : 0)} pkt/s`;

const reportPercent = (value: number): string => `${value.toFixed(value >= 10 ? 1 : 2)}%`;

const reportTime = (value: string): string => {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return reportShort(value, 18);
  return new Date(timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const reportDateTime = (value: string): string => {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return reportShort(value, 28);
  return new Date(timestamp).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const pdfFill = (x: number, y: number, width: number, height: number, color: string): string => `${color} rg ${x} ${y} ${width} ${height} re f\n`;

const pdfReportTable = (
  x: number,
  top: number,
  width: number,
  columns: PdfTableColumn[],
  rows: string[][],
  rowHeight = 22,
  fontSize = 8.2
): string => {
  const total = columns.reduce((sum, column) => sum + column.width, 0) || 1;
  const scaledColumns = columns.map((column) => ({ ...column, width: column.width / total * width }));
  let cursor = x;
  let content = pdfFill(x, top - rowHeight + 5, width, rowHeight, '0.95 0.97 0.98');
  scaledColumns.forEach((column) => {
    const cellRight = cursor + column.width - 6;
    const baseline = top - rowHeight + 12;
    if (column.align === 'right') content += pdfTextRight(cellRight, baseline, column.label, fontSize, true);
    else if (column.align === 'center') content += pdfText(cursor + (column.width - column.label.length * fontSize * 0.57) / 2, baseline, column.label, fontSize, true);
    else content += pdfText(cursor + 6, baseline, column.label, fontSize, true);
    cursor += column.width;
  });
  rows.forEach((row, rowIndex) => {
    const baseline = top - rowHeight * (rowIndex + 2) + 12;
    cursor = x;
    content += pdfRule(x, baseline - 7, x + width, baseline - 7, '0.87 0.90 0.92', 0.5);
    scaledColumns.forEach((column, columnIndex) => {
      if (column.swatch) {
        const swatchSize = Math.min(9, Math.max(6, column.width - 8));
        content += pdfFill(cursor + (column.width - swatchSize) / 2, baseline - 5, swatchSize, swatchSize, row[columnIndex] ?? '0.58 0.64 0.70');
        cursor += column.width;
        return;
      }
      const value = reportShort(row[columnIndex] ?? '', Math.max(8, Math.round(column.width / (fontSize * 0.52))));
      const cellRight = cursor + column.width - 6;
      const primaryLabel = columnIndex === 0 || (columnIndex === 1 && scaledColumns[0]?.swatch);
      if (column.align === 'right') content += pdfTextRight(cellRight, baseline, value, fontSize, primaryLabel);
      else if (column.align === 'center') content += pdfText(cursor + (column.width - value.length * fontSize * 0.57) / 2, baseline, value, fontSize, primaryLabel);
      else content += pdfText(cursor + 6, baseline, value, fontSize, primaryLabel);
      cursor += column.width;
    });
  });
  return content;
};

const pdfReportChart = (
  x: number,
  y: number,
  width: number,
  height: number,
  series: PdfChartSeries[],
  startTime: string,
  endTime: string,
  valueFormatter: (value: number) => string
): string => {
  const allValues = series.flatMap((item) => item.values);
  const maximum = Math.max(...allValues, 1);
  let content = pdfFill(x, y, width, height, '0.99 1 1');
  [0, 0.5, 1].forEach((fraction) => {
    const gridY = y + height * fraction;
    content += pdfRule(x, gridY, x + width, gridY, '0.86 0.90 0.93', fraction === 0 ? 0.8 : 0.55);
    content += pdfTextRight(x - 7, gridY - 3, valueFormatter(maximum * fraction), 6.5);
  });
  series.forEach((item) => {
    if (item.values.length === 0) return;
    const points = item.values.map((value, index) => {
      const pointX = x + index / Math.max(1, item.values.length - 1) * width;
      const pointY = y + Math.max(0, value) / maximum * height;
      return `${pointX.toFixed(1)} ${pointY.toFixed(1)} ${index === 0 ? 'm' : 'l'}`;
    }).join(' ');
    content += `${item.color} RG 1.15 w ${points} S\n`;
  });
  content += pdfText(x, y - 16, reportTime(startTime), 7);
  content += pdfTextRight(x + width, y - 16, reportTime(endTime), 7);
  return content;
};

const pdfDonut = (centerX: number, centerY: number, outerRadius: number, innerRadius: number, items: Array<{ value: number; color: string }>): string => {
  const total = items.reduce((sum, item) => sum + Math.max(0, item.value), 0) || 1;
  let angle = Math.PI / 2;
  let content = '';
  items.forEach((item) => {
    const slice = Math.max(0, item.value) / total * Math.PI * 2;
    const end = angle - slice;
    const steps = Math.max(3, Math.ceil(slice / (Math.PI / 20)));
    const points: string[] = [];
    for (let index = 0; index <= steps; index += 1) {
      const current = angle + (end - angle) * index / steps;
      points.push(`${(centerX + Math.cos(current) * outerRadius).toFixed(2)} ${(centerY + Math.sin(current) * outerRadius).toFixed(2)}`);
    }
    for (let index = steps; index >= 0; index -= 1) {
      const current = angle + (end - angle) * index / steps;
      points.push(`${(centerX + Math.cos(current) * innerRadius).toFixed(2)} ${(centerY + Math.sin(current) * innerRadius).toFixed(2)}`);
    }
    content += `${item.color} rg ${points[0]} m ${points.slice(1).map((point) => `${point} l`).join(' ')} h f\n`;
    angle = end;
  });
  return content;
};

const pdfReportPage = (title: string, subtitle: string, pageNumber: number, totalPages: number, body: string): string => [
  pdfFill(0, 742, 612, 50, '0.97 0.99 1'),
  pdfFill(58, 751, 4, 12, '0.08 0.57 0.82'),
  pdfText(70, 755, 'REAP', 10, true),
  pdfTextColor(104, 755, 'FLOW ANALYTICS', 8, true, '0.28 0.37 0.43'),
  pdfTextRight(554, 755, 'Network traffic intelligence report', 7.5),
  pdfText(58, 720, title, 19, true),
  pdfTextColor(58, 704, subtitle, 8.5, false, '0.32 0.39 0.45'),
  pdfRule(58, 694, 554, 694),
  body,
  pdfRule(58, 42, 554, 42),
  pdfTextColor(58, 28, 'REAP Flow Analytics - generated from the selected query.', 7, false, '0.32 0.39 0.45'),
  pdfTextRight(554, 28, `Page ${pageNumber} of ${totalPages}`, 7)
].join('');

const pdfReportCover = (
  report: MockFlowReport,
  scope: string,
  overview: PdfReportOverview,
  rangeStart: string,
  rangeEnd: string,
  totalPages: number
): string => [
  pdfFill(0, 0, 612, 792, '0.025 0.075 0.11'),
  pdfFill(0, 0, 14, 792, '0.08 0.57 0.82'),
  pdfFill(58, 675, 7, 42, '0.20 0.72 0.94'),
  pdfTextColor(78, 704, 'REAP', 23, true, '1 1 1'),
  pdfTextColor(78, 683, 'NETWORK INTELLIGENCE', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(58, 606, 'Flow Analytics', 30, true, '1 1 1'),
  pdfTextColor(58, 570, 'Network traffic report', 18, false, '0.73 0.84 0.90'),
  pdfTextColor(58, 530, 'A detailed operator view of observed traffic, ranked activity,', 10, false, '0.73 0.84 0.90'),
  pdfTextColor(58, 514, 'exporter evidence, network dimensions, and interface context.', 10, false, '0.73 0.84 0.90'),
  pdfFill(58, 382, 496, 96, '0.055 0.15 0.21'),
  pdfTextColor(78, 449, 'REPORT SCOPE', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(78, 426, scope, 15, true, '1 1 1'),
  pdfTextColor(78, 401, 'Query interval', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(78, 385, `${reportDateTime(rangeStart)} - ${reportDateTime(rangeEnd)}`, 9, false, '0.87 0.93 0.96'),
  pdfTextColor(58, 326, 'OBSERVED BYTES', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(58, 300, reportBytes(overview.totalBytes), 19, true, '1 1 1'),
  pdfTextColor(226, 326, 'OBSERVED PACKETS', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(226, 300, reportPackets(overview.totalPackets), 19, true, '1 1 1'),
  pdfTextColor(394, 326, 'SAMPLING', 8, true, '0.38 0.77 0.94'),
  pdfTextColor(394, 300, overview.metadata.samplingState.toUpperCase(), 15, true, '1 1 1'),
  pdfRule(58, 248, 554, 248, '0.16 0.29 0.37', 0.7),
  pdfTextColor(58, 224, `Generated ${reportDateTime(report.createdAt)}`, 8, false, '0.58 0.70 0.77'),
  pdfTextColor(58, 205, `Time-series resolution ${overview.metadata.timeSeriesResolution}; ${overview.observedRecordCount} observed flow records.`, 8, false, '0.58 0.70 0.77'),
  pdfTextColor(58, 70, 'CONFIDENTIAL - FOR AUTHORIZED NETWORK OPERATIONS USE', 7, true, '0.38 0.77 0.94'),
  pdfTextRight(554, 70, `Report 1 of ${totalPages}`, 7, false, '0.58 0.70 0.77')
].join('');

const pdfReportContents = (entries: Array<{ title: string; subtitle: string }>, totalPages: number): string => {
  let content = [
    pdfFill(0, 742, 612, 50, '0.97 0.99 1'),
    pdfFill(58, 751, 4, 12, '0.08 0.57 0.82'),
    pdfText(70, 755, 'REAP', 10, true),
    pdfTextColor(104, 755, 'FLOW ANALYTICS', 8, true, '0.28 0.37 0.43'),
    pdfText(58, 700, 'Report contents', 25, true),
    pdfTextColor(58, 680, 'Use this index to navigate the detailed traffic and evidence sections.', 9, false, '0.32 0.39 0.45'),
    pdfTextColor(58, 642, 'SECTION', 8, true, '0.32 0.39 0.45'),
    pdfTextRight(554, 642, 'PAGE', 8, true),
    pdfRule(58, 632, 554, 632)
  ].join('');
  entries.forEach((entry, index) => {
    const y = 602 - index * 51;
    const page = index + 3;
    content += pdfText(58, y, entry.title, 11, true);
    content += pdfTextColor(58, y - 16, entry.subtitle, 8, false, '0.32 0.39 0.45');
    content += pdfTextRight(554, y, String(page), 11, true);
    content += pdfRule(58, y - 27, 554, y - 27, '0.87 0.90 0.92', 0.55);
  });
  return content + [
    pdfRule(58, 42, 554, 42),
    pdfTextColor(58, 28, 'REAP Flow Analytics report', 7, false, '0.32 0.39 0.45'),
    pdfTextRight(554, 28, `Page 2 of ${totalPages}`, 7)
  ].join('');
};

const reportUrl = (report: MockFlowReport, path: string, parameters: Record<string, string> = {}): URL => {
  const url = new URL(`https://mock.reapitnow.local/v1/orgs/${encodeURIComponent(report.orgId)}/flows/${path}`);
  url.searchParams.set('startTime', report.startTime);
  url.searchParams.set('endTime', report.endTime);
  if (report.siteId) url.searchParams.set('siteId', report.siteId);
  Object.entries(parameters).forEach(([key, value]) => url.searchParams.set(key, value));
  return url;
};

const pdfMetric = (x: number, label: string, value: string, note?: string): string => [
  pdfText(x, 654, label, 8, true),
  pdfText(x, 632, value, 16, true),
  ...(note ? [pdfText(x, 618, note, 7.5)] : [])
].join('');

const conversationLabel = (conversation: FlowConversation): string => `${conversation.sourceAddress} <-> ${conversation.destinationAddress}`;

const pdfConversationEvidence = (conversation: FlowConversation, top: number): string => {
  const paths = conversation.directionalPaths ?? [];
  const cardBottom = top - 102;
  let content = pdfFill(58, cardBottom, 496, 96, '0.985 0.99 1');
  content += pdfText(68, top - 21, conversationLabel(conversation), 10, true);
  content += pdfText(68, top - 37, `Exporter ${conversation.exporter.samplerAddress} - ${conversation.exporter.flowType} - OD ${conversation.exporter.observationDomainId}`, 7.5);
  content += pdfTextRight(544, top - 21, `${reportBytes(conversation.bytes)} - ${reportPackets(conversation.packets)} pkts`, 8, true);
  if (paths.length === 0) return content + pdfText(68, top - 62, 'No exporter-observed interface path is available for this conversation.', 8);
  paths.slice(0, 3).forEach((path, index) => {
    const direction = path.direction === 'first_to_second' ? 'First -> second' : 'Second -> first';
    const ingress = path.ingressInterfaceName ?? `ifIndex ${path.ingressIfIndex ?? '-'}`;
    const egress = path.egressInterfaceName ?? `ifIndex ${path.egressIfIndex ?? '-'}`;
    const baseline = top - 56 - index * 13;
    content += pdfText(76, baseline, reportShort(`${direction}: ${ingress} -> ${egress}`, 68), 7.4);
    content += pdfTextRight(544, baseline, `${reportBytes(path.bytes)} - ${reportPackets(path.packets)} pkts`, 7.4);
  });
  return content;
};

const mockPdf = (report: MockFlowReport): string => {
  const reportOverview = overview(reportUrl(report, 'overview', { topLimit: '10' }));
  const reportConversations = conversations(reportUrl(report, 'conversations', { mode: 'endpoint_pair', limit: '10' }));
  const reportEndpoints = endpoints(reportUrl(report, 'endpoints', { limit: '10' }));
  const reportServices = services(reportUrl(report, 'services', { limit: '10' }));
  const reportSources = sources(reportUrl(report, 'sources', { limit: '20' }));
  const rateValues = reportOverview.timeSeries.map((bucket) => bucket.observedBytesPerSecond);
  const packetRateValues = reportOverview.timeSeries.map((bucket) => bucket.observedPacketsPerSecond);
  const rangeStart = reportOverview.metadata.effectiveStartTime;
  const rangeEnd = reportOverview.metadata.effectiveEndTime;
  const scope = report.siteId ? `Site ${report.siteId}` : 'Entire organization';

  const overviewBody = [
    pdfText(58, 682, 'Selected scope', 8, true),
    pdfText(58, 666, scope, 10, true),
    pdfText(236, 682, 'Query range', 8, true),
    pdfText(236, 666, `${reportDateTime(rangeStart)} - ${reportDateTime(rangeEnd)}`, 8.5),
    pdfText(58, 592, 'Observed traffic summary', 13, true),
    pdfMetric(58, 'OBSERVED BYTES', reportBytes(reportOverview.totalBytes), `${reportOverview.observedRecordCount} flow records`),
    pdfMetric(202, 'OBSERVED PACKETS', reportPackets(reportOverview.totalPackets), `${reportRate(reportOverview.observedBytesPerSecond)} average`),
    pdfMetric(346, 'SAMPLING', reportOverview.metadata.samplingState.toUpperCase(), `${reportOverview.metadata.timeSeriesResolution} resolution`),
    pdfText(58, 570, 'Traffic rate', 11, true),
    pdfText(58, 556, `Average ${reportRate(reportOverview.observedBytesPerSecond)} across the selected range`, 8),
    pdfReportChart(76, 410, 460, 126, [{ color: reportColors[0]!, values: rateValues }], rangeStart, rangeEnd, reportRate),
    pdfText(58, 374, 'Packet rate', 11, true),
    pdfText(58, 360, `Average ${reportPacketRate(reportOverview.observedPacketsPerSecond)} across the selected range`, 8),
    pdfReportChart(76, 214, 460, 126, [{ color: reportColors[1]!, values: packetRateValues }], rangeStart, rangeEnd, reportPacketRate),
    pdfText(58, 150, 'Both traffic-rate and packet-rate charts use the report request time range, not the dashboard slider state.', 8)
  ].join('');

  const conversationBody = [
    pdfText(58, 679, 'Each line is a ranked endpoint-pair result. The table includes all top-ranked pairs in this report range.', 8),
    pdfReportChart(76, 460, 460, 162, reportConversations.items.map((item, index) => ({
      color: reportColors[index % reportColors.length]!,
      values: item.timeSeries.map((bucket) => bucket.observedBytesPerSecond)
    })), rangeStart, rangeEnd, reportRate),
    pdfReportTable(58, 420, 496, [
      { label: 'Colour', width: 30, align: 'center', swatch: true },
      { label: 'Conversation', width: 215 },
      { label: 'Observed bytes', width: 91, align: 'right' },
      { label: 'Packets', width: 75, align: 'right' },
      { label: 'Share', width: 85, align: 'right' }
    ], reportConversations.items.map((item, index) => [
      reportColors[index % reportColors.length]!, conversationLabel(item), reportBytes(item.bytes), reportPackets(item.packets), reportPercent(item.trafficSharePercent)
    ]), 22, 8)
  ].join('');

  const conversationEvidence = (items: FlowConversation[]): string => [
    pdfText(58, 681, 'Directional paths show the input-to-output interface evidence observed by the exporter for each endpoint pair.', 8),
    ...items.map((item, index) => pdfConversationEvidence(item, 655 - index * 112))
  ].join('');

  const endpointBody = [
    pdfReportChart(76, 467, 460, 154, reportEndpoints.items.map((item, index) => ({
      color: reportColors[index % reportColors.length]!,
      values: item.timeSeries.map((bucket) => bucket.observedBytesPerSecond)
    })), rangeStart, rangeEnd, reportRate),
    pdfReportTable(58, 426, 496, [
      { label: 'Colour', width: 30, align: 'center', swatch: true },
      { label: 'Endpoint', width: 160 },
      { label: 'Sent', width: 72, align: 'right' },
      { label: 'Received', width: 84, align: 'right' },
      { label: 'Packets', width: 70, align: 'right' },
      { label: 'Share', width: 80, align: 'right' }
    ], reportEndpoints.items.map((item, index) => [
      reportColors[index % reportColors.length]!, item.address, reportBytes(item.sentBytes), reportBytes(item.receivedBytes), reportPackets(item.packets), reportPercent(item.trafficSharePercent)
    ]), 22, 8)
  ].join('');

  const serviceBody = [
    pdfReportChart(76, 467, 460, 154, reportServices.items.map((item, index) => ({
      color: reportColors[index % reportColors.length]!,
      values: item.timeSeries.map((bucket) => bucket.observedBytesPerSecond)
    })), rangeStart, rangeEnd, reportRate),
    pdfReportTable(58, 426, 496, [
      { label: 'Colour', width: 30, align: 'center', swatch: true },
      { label: 'Service', width: 148 },
      { label: 'To service', width: 82, align: 'right' },
      { label: 'From service', width: 88, align: 'right' },
      { label: 'Packets', width: 68, align: 'right' },
      { label: 'Share', width: 80, align: 'right' }
    ], reportServices.items.map((item, index) => [
      reportColors[index % reportColors.length]!, `${item.ianaServiceName ?? item.label} - ${item.label}`, reportBytes(item.toServiceBytes), reportBytes(item.fromServiceBytes), reportPackets(item.packets), reportPercent(item.trafficSharePercent)
    ]), 22, 8)
  ].join('');

  const sourceBody = [
    pdfText(58, 679, 'Expanded source evidence includes the ingress and egress interface totals used by the Flow sources card.', 8),
    ...reportSources.items.slice(0, 3).flatMap((source, sourceIndex) => {
      const top = 654 - sourceIndex * 184;
      const sourceInterfaces = interfaceTotals(reportUrl(report, 'interface-totals', {
        samplerAddress: source.samplerAddress,
        flowType: source.flowType,
        observationDomainId: String(source.observationDomainId),
        limit: '10'
      })).items.slice(0, 4);
      return [
        pdfFill(58, top - 166, 496, 158, '0.985 0.99 1'),
        pdfText(68, top - 25, `${source.samplerAddress} - ${source.flowType} - OD ${source.observationDomainId}`, 10, true),
        pdfTextRight(544, top - 25, `${reportBytes(source.bytes)} - ${reportPackets(source.packets)} pkts`, 8, true),
        pdfText(68, top - 41, `Sampling ${source.samplingState}; ${source.recordCount} records; last observed ${reportTime(source.lastSeenAt)}`, 7.5),
        pdfReportTable(68, top - 54, 476, [
          { label: 'Interface', width: 210 },
          { label: 'Ingress', width: 92, align: 'right' },
          { label: 'Egress', width: 92, align: 'right' },
          { label: 'Packets', width: 82, align: 'right' }
        ], sourceInterfaces.map((item) => [
          `${item.interfaceName ?? 'Unknown interface'} - ifIndex ${item.ifIndex}`,
          reportBytes(item.ingressBytes), reportBytes(item.egressBytes), reportPackets(item.totalPackets)
        ]), 19, 7.3)
      ];
    })
  ].join('');

  const protocolRows = reportOverview.topProtocols.map((item, index) => ({
    color: reportColors[index % reportColors.length]!,
    label: item.key.toUpperCase(),
    share: reportPercent(item.trafficSharePercent),
    bytes: reportBytes(item.bytes),
    packets: reportPackets(item.packets),
    averageRate: reportRate(item.observedBytesPerSecond)
  }));
  const protocolBody = [
    pdfDonut(150, 555, 72, 42, reportOverview.topProtocols.map((item, index) => ({ value: item.bytes, color: reportColors[index % reportColors.length]! }))),
    pdfText(124, 558, reportBytes(reportOverview.totalBytes), 12, true),
    pdfText(126, 544, 'observed', 7),
    pdfReportTable(254, 625, 300, [
      { label: 'Colour', width: 28, align: 'center', swatch: true },
      { label: 'Protocol', width: 88 },
      { label: 'Share', width: 50, align: 'right' },
      { label: 'Bytes', width: 72, align: 'right' },
      { label: 'Packets', width: 62, align: 'right' }
    ], protocolRows.map((row) => [row.color, row.label, row.share, row.bytes, row.packets]), 25, 7.8),
    pdfText(58, 428, 'Protocol detail', 13, true),
    ...protocolRows.slice(0, 5).map((row, index) => {
      const top = 400 - index * 53;
      return [
        pdfFill(58, top - 38, 496, 40, index % 2 === 0 ? '0.985 0.99 1' : '1 1 1'),
        pdfFill(68, top - 22, 7, 7, row.color),
        pdfText(82, top - 15, row.label, 10, true),
        pdfText(150, top - 15, `Traffic share ${row.share}`, 8),
        pdfText(284, top - 15, `Average rate ${row.averageRate}`, 8),
        pdfText(425, top - 15, `${row.bytes} - ${row.packets} pkts`, 8, true),
        pdfText(68, top - 29, `Scope: ${scope}; selected time range ${reportTime(rangeStart)} - ${reportTime(rangeEnd)}; sampling ${reportOverview.metadata.samplingState}.`, 7)
      ].join('');
    })
  ].join('');

  const nextHopMetrics = [
    { label: 'Known next hop', metric: reportOverview.nextHopCoverage.known },
    { label: '0.0.0.0', metric: reportOverview.nextHopCoverage.zeroAddress },
    { label: 'Unknown / missing', metric: reportOverview.nextHopCoverage.unavailable }
  ].map(({ label, metric: item }, index) => ({
    color: reportColors[index % reportColors.length]!,
    key: label,
    bytes: item.bytes,
    packets: item.packets,
    trafficSharePercent: item.trafficSharePercent,
    observedBytesPerSecond: item.observedBytesPerSecond
  }));
  const dimensionBody = [
    pdfText(58, 681, 'Next-hop coverage and ASN values are reported directly by the exporter records in the selected range.', 8),
    pdfDonut(150, 550, 70, 40, nextHopMetrics.map((item, index) => ({ value: item.bytes, color: reportColors[index % reportColors.length]! }))),
    pdfText(123, 553, reportBytes(reportOverview.totalBytes), 11, true),
    pdfText(125, 539, 'total bytes', 6.5),
    pdfReportTable(250, 616, 304, [
      { label: 'Colour', width: 28, align: 'center', swatch: true },
      { label: 'Next hop', width: 102 },
      { label: 'Bytes', width: 80, align: 'right' },
      { label: 'Share', width: 94, align: 'right' }
    ], nextHopMetrics.map((item) => [item.color, item.key, reportBytes(item.bytes), reportPercent(item.trafficSharePercent)]), 23, 7.8),
    pdfText(58, 392, 'Autonomous systems (ASNs)', 13, true),
    pdfText(58, 376, 'Source and destination ASNs when present in flow records.', 8),
    pdfReportTable(58, 350, 238, [
      { label: 'Source ASN', width: 96 },
      { label: 'Bytes', width: 78, align: 'right' },
      { label: 'Share', width: 64, align: 'right' }
    ], reportOverview.topSourceAsns.slice(0, 6).map((item) => [`AS${item.key}`, reportBytes(item.bytes), reportPercent(item.trafficSharePercent)]), 22, 7.5),
    pdfReportTable(316, 350, 238, [
      { label: 'Destination ASN', width: 106 },
      { label: 'Bytes', width: 70, align: 'right' },
      { label: 'Share', width: 62, align: 'right' }
    ], reportOverview.topDestinationAsns.slice(0, 6).map((item) => [`AS${item.key}`, reportBytes(item.bytes), reportPercent(item.trafficSharePercent)]), 22, 7.5)
  ].join('');

  const interfacesForReport = interfaceTotals(reportUrl(report, 'interface-totals', { limit: '10' })).items;
  const interfaceBody = [
    pdfText(58, 681, 'Ingress and egress are counted from the same Flow records shown elsewhere in this report; they are not additional traffic.', 8),
    pdfReportTable(58, 650, 496, [
      { label: 'Exporter / interface', width: 210 },
      { label: 'Ingress', width: 85, align: 'right' },
      { label: 'Egress', width: 85, align: 'right' },
      { label: 'In + out', width: 80, align: 'right' },
      { label: 'Packets', width: 72, align: 'right' }
    ], interfacesForReport.map((item) => [
      `${item.exporter.samplerAddress} - ${item.interfaceName ?? 'Unknown interface'} (ifIndex ${item.ifIndex})`,
      reportBytes(item.ingressBytes), reportBytes(item.egressBytes), reportBytes(item.totalBytes), reportPackets(item.totalPackets)
    ]), 31, 7.5),
    pdfText(58, 208, 'Report coverage', 13, true),
    pdfText(58, 189, `Sampling state: ${reportOverview.metadata.samplingState}. Time-series resolution: ${reportOverview.metadata.timeSeriesResolution}.`, 8.5),
    pdfText(58, 173, `Visible dashboard range at request time: ${reportDateTime(report.visibleStartTime)} - ${reportDateTime(report.visibleEndTime)}.`, 8.5),
    pdfText(58, 157, 'The report includes ranked traffic, expanded path evidence, source interfaces, protocol detail, next hops, ASNs, and interface totals.', 8.5)
  ].join('');

  const bodyPages = [
    { title: 'Overview and traffic over time', subtitle: 'Traffic rate and packet rate across the complete selected query range.', content: overviewBody },
    { title: 'Top 10 conversations', subtitle: 'Endpoint-pair traffic rate chart and ranked traffic totals.', content: conversationBody },
    { title: 'Conversation path evidence (1 of 2)', subtitle: 'Expanded exporter-observed interface paths for the first ranked conversations.', content: conversationEvidence(reportConversations.items.slice(0, 5)) },
    { title: 'Conversation path evidence (2 of 2)', subtitle: 'Expanded exporter-observed interface paths for the remaining ranked conversations.', content: conversationEvidence(reportConversations.items.slice(5, 10)) },
    { title: 'Top 10 endpoints', subtitle: 'Endpoint traffic-rate chart with sent, received, packet, and share totals.', content: endpointBody },
    { title: 'Top 10 services', subtitle: 'Service traffic-rate chart with to-service and from-service totals.', content: serviceBody },
    { title: 'Flow sources and interface evidence', subtitle: 'Expanded source detail with exporter-observed ingress and egress interfaces.', content: sourceBody },
    { title: 'Observed protocols', subtitle: 'Protocol composition doughnut, ranked totals, and the selected-protocol detail context.', content: protocolBody },
    { title: 'Network dimensions', subtitle: 'Next-hop coverage doughnut plus source and destination ASN evidence.', content: dimensionBody },
    { title: 'Interface traffic and report coverage', subtitle: 'Exporter-interface totals and query coverage information.', content: interfaceBody }
  ];
  const totalPages = bodyPages.length + 2;
  return buildMockPdf([
    pdfReportCover(report, scope, reportOverview, rangeStart, rangeEnd, totalPages),
    pdfReportContents(bodyPages, totalPages),
    ...bodyPages.map((page, index) => pdfReportPage(page.title, page.subtitle, index + 3, totalPages, page.content))
  ]);
};

const flowReportId = (pathname: string): string | null => {
  const match = pathname.match(/\/flows\/reports\/([^/]+)(?:\/download)?$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const invalidRequest = (detail: string) => json({
  type: 'invalid-request',
  title: 'Invalid request',
  status: 400,
  detail
}, 400);

const queryLimit = (url: URL, name: string, fallback: number): number => {
  const value = Number.parseInt(url.searchParams.get(name) ?? '', 10);
  return Number.isSafeInteger(value) && value > 0 ? Math.min(value, 200) : fallback;
};

const topTenRequestError = (url: URL): string | null => {
  if (url.searchParams.get('after') || url.searchParams.get('before')) {
    return 'cursor pagination is not supported by this endpoint';
  }
  const rawLimit = url.searchParams.get('limit')?.trim();
  if (!rawLimit) return null;
  const limit = Number(rawLimit);
  return Number.isSafeInteger(limit) && limit >= 1 && limit <= 10 ? null : 'limit must be between 1 and 10';
};

// topTenPage mirrors the dashboard-only ranking endpoints. Unlike raw search
// and source/interface lists, these results are capped snapshots and expose no
// cursors or exact total because the aggregate may only inspect its Top-N set.
const topTenPage = <T>(url: URL, items: T[]) => {
  const limit = Number(url.searchParams.get('limit'));
  const resolvedLimit = Number.isSafeInteger(limit) && limit >= 1 && limit <= 10 ? limit : 10;
  return {
    items: items.slice(0, resolvedLimit),
    meta: { limit: resolvedLimit }
  };
};

const rank = <T extends { bytes: number; packets: number }>(items: T[], key: (item: T) => string, limit: number): T[] =>
  items
    .sort((left, right) => right.bytes - left.bytes || right.packets - left.packets || key(left).localeCompare(key(right)))
    .slice(0, limit);

const metrics = (selectedRecords: MockFlowRecord[], keyForRecord: (record: MockFlowRecord) => string, durationSeconds: number): FlowMetric[] => {
  const items = new Map<string, FlowMetric>();
  const totalBytes = selectedRecords.reduce((total, record) => total + record.bytes, 0);
  for (const record of selectedRecords) {
    const key = keyForRecord(record);
    const item = items.get(key) ?? { key, bytes: 0, packets: 0, recordCount: 0, observedBytesPerSecond: 0, trafficSharePercent: 0 };
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
    items.set(key, item);
  }
  return rank([...items.values()], (item) => item.key, Number.MAX_SAFE_INTEGER)
    .map((item) => ({
      ...item,
      observedBytesPerSecond: durationSeconds > 0 ? item.bytes / durationSeconds : 0,
      trafficSharePercent: totalBytes > 0 ? item.bytes / totalBytes * 100 : 0
    }));
};

// nextHopCoverage mirrors the complete-range server aggregate. It remains
// separate from the bounded topNextHops ranking so mock rendering cannot hide
// unavailable or known traffic outside the ranking limit.
const nextHopCoverage = (selectedRecords: MockFlowRecord[], durationSeconds: number) => {
  const category = (record: MockFlowRecord): string => {
    const value = record.nextHop.trim().toLowerCase();
    if (value === '' || value === 'unknown' || value === 'unavailable') return 'unavailable';
    if (value === '0.0.0.0') return 'zero_address';
    return 'known';
  };
  const values = metrics(selectedRecords, category, durationSeconds);
  const metric = (key: string, label: string): FlowMetric => values.find((item) => item.key === key) ?? {
    key: label,
    bytes: 0,
    packets: 0,
    recordCount: 0,
    observedBytesPerSecond: 0,
    trafficSharePercent: 0
  };
  return {
    known: metric('known', 'known'),
    zeroAddress: metric('zero_address', '0.0.0.0'),
    unavailable: metric('unavailable', 'unavailable')
  };
};

const exporterForRecord = (record: MockFlowRecord): FlowExporterIdentity => ({
  flowType: record.flowType,
  samplerAddress: record.samplerAddress,
  observationDomainId: record.observationDomainId
});

const exporterKey = (record: MockFlowRecord): string =>
  [record.flowType, record.samplerAddress, record.observationDomainId].join('|');

const numericQuery = (url: URL, name: string): number | undefined => {
  const value = url.searchParams.get(name);
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const matchesString = (url: URL, name: string, value: string): boolean => {
  const filter = url.searchParams.get(name);
  return filter == null || filter === '' || filter === value;
};

const matchesNumber = (url: URL, name: string, value: number | undefined): boolean => {
  const filter = numericQuery(url, name);
  return filter == null || (value != null && filter === value);
};

const matchesEventTime = (url: URL, eventTime: string): boolean => {
  const timestamp = Date.parse(eventTime);
  const start = Date.parse(url.searchParams.get('startTime') ?? '');
  const end = Date.parse(url.searchParams.get('endTime') ?? '');
  return (Number.isNaN(start) || timestamp >= start) && (Number.isNaN(end) || timestamp < end);
};

const matchesEndpointPair = (url: URL, record: MockFlowRecord): boolean => {
  const endpointA = url.searchParams.get('endpointA');
  const endpointB = url.searchParams.get('endpointB');
  if (!endpointA && !endpointB) return true;
  if (!endpointA || !endpointB) return false;
  return (record.sourceAddress === endpointA && record.destinationAddress === endpointB)
    || (record.sourceAddress === endpointB && record.destinationAddress === endpointA);
};

const mockSiteId = (record: MockFlowRecord): string => {
  if (record.samplerAddress === '10.20.0.6') return 'site-sfo';
  if (record.samplerAddress === '10.20.0.9') return 'site-ams';
  return 'site-nyc';
};

const selectedRecords = (url: URL): MockFlowRecord[] => records.filter((record) =>
  matchesEventTime(url, record.flowEndTime) &&
  (!url.searchParams.has('siteId') || url.searchParams.get('siteId') === mockSiteId(record)) &&
  matchesString(url, 'flowType', record.flowType) &&
  matchesString(url, 'samplerAddress', record.samplerAddress) &&
  matchesNumber(url, 'observationDomainId', record.observationDomainId) &&
  matchesString(url, 'sourceAddress', record.sourceAddress) &&
  matchesString(url, 'destinationAddress', record.destinationAddress) &&
  matchesString(url, 'protocol', record.protocol ?? '') &&
  matchesNumber(url, 'sourcePort', record.sourcePort) &&
  matchesNumber(url, 'destinationPort', record.destinationPort) &&
  matchesNumber(url, 'vlanId', record.vlanId) &&
  matchesNumber(url, 'sourceAs', record.sourceAs) &&
  matchesNumber(url, 'destinationAs', record.destinationAs) &&
  matchesString(url, 'nextHop', record.nextHop) &&
  matchesString(url, 'sourceNetwork', record.sourceNetwork) &&
  matchesString(url, 'destinationNetwork', record.destinationNetwork) &&
  matchesEndpointPair(url, record) &&
  matchesNumber(url, 'tcpFlags', record.tcpFlags) &&
  matchesNumber(url, 'icmpType', record.icmpType) &&
  matchesNumber(url, 'icmpCode', record.icmpCode) &&
  matchesNumber(url, 'forwardingStatus', record.forwardingStatus) &&
  matchesNumber(url, 'ipTos', record.ipTos) &&
  matchesNumber(url, 'ipTtl', record.ipTtl) &&
  matchesString(url, 'etherType', record.etherType) &&
  (() => {
    const port = numericQuery(url, 'port');
    return port == null || record.sourcePort === port || record.destinationPort === port;
  })()
);

const page = <T>(url: URL, items: T[]) => {
  const limit = queryLimit(url, 'limit', 50);
  const after = numericQuery(url, 'after');
  const before = numericQuery(url, 'before');
  const end = before == null ? items.length : Math.min(items.length, Math.max(0, Math.floor(before)));
  const offset = after == null
    ? before == null ? 0 : Math.max(0, end - limit)
    : Math.min(end, Math.max(0, Math.floor(after)));
  const slice = items.slice(offset, Math.min(end, offset + limit));
  const nextOffset = offset + slice.length;
  return {
    items: slice,
    meta: {
      limit,
      exactTotal: items.length,
      ...(nextOffset < items.length ? { next: String(nextOffset) } : {}),
      ...(offset > 0 ? { prev: String(Math.max(0, offset - limit)) } : {})
    }
  };
};

const delaySummary = (selected: MockFlowRecord[]) => {
  const sorted = selected.map((record) => record.observedDelayNs).sort((left, right) => left - right);
  const percentile = (percent: number) => sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * percent))];
  const total = sorted.reduce((sum, value) => sum + value, 0);
  return sorted.length === 0
    ? { recordCount: 0 }
    : {
      recordCount: sorted.length,
      minNs: sorted[0],
      p50Ns: percentile(0.5),
      p95Ns: percentile(0.95),
      maxNs: sorted[sorted.length - 1],
      averageNs: Math.round(total / sorted.length)
    };
};

const timeSeries = (selected: MockFlowRecord[], bounds?: Pick<FlowMetadata, 'effectiveStartTime' | 'effectiveEndTime'>): FlowTimeBucket[] => {
  const buckets = new Map<string, FlowTimeBucket>();
  const rangeStart = Date.parse(bounds?.effectiveStartTime ?? '');
  const rangeEnd = Date.parse(bounds?.effectiveEndTime ?? '');
  const hasBounds = !Number.isNaN(rangeStart) && !Number.isNaN(rangeEnd) && rangeEnd > rangeStart;
  const intervalMilliseconds = hasBounds
    ? timeSeriesIntervalMilliseconds(bounds!.effectiveStartTime, bounds!.effectiveEndTime)
    : 60_000;
  const addBucket = (nominalStart: number): FlowTimeBucket => {
    const key = new Date(nominalStart).toISOString();
    const existing = buckets.get(key);
    if (existing) return existing;
    const start = hasBounds ? Math.max(nominalStart, rangeStart) : nominalStart;
    const end = hasBounds ? Math.min(nominalStart + intervalMilliseconds, rangeEnd) : nominalStart + intervalMilliseconds;
    const bucket: FlowTimeBucket = {
      startTime: new Date(start).toISOString(),
      endTime: new Date(end).toISOString(),
      bucketDurationSeconds: (end - start) / 1000,
      bytes: 0,
      packets: 0,
      observedBytesPerSecond: 0,
      observedPacketsPerSecond: 0,
      recordCount: 0
    };
    buckets.set(key, bucket);
    return bucket;
  };
  for (const record of selected) {
    const nominalStart = Math.floor(Date.parse(record.flowEndTime) / intervalMilliseconds) * intervalMilliseconds;
    const item = addBucket(nominalStart);
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
  }
  if (hasBounds) {
    const firstBucket = Math.floor(rangeStart / intervalMilliseconds) * intervalMilliseconds;
    const lastBucket = Math.floor((rangeEnd - 1) / intervalMilliseconds) * intervalMilliseconds;
    for (let bucketStart = firstBucket; bucketStart <= lastBucket; bucketStart += intervalMilliseconds) {
      addBucket(bucketStart);
    }
  }
  return [...buckets.values()]
    .map((item) => ({
      ...item,
      observedBytesPerSecond: item.bucketDurationSeconds > 0 ? item.bytes / item.bucketDurationSeconds : 0,
      observedPacketsPerSecond: item.bucketDurationSeconds > 0 ? item.packets / item.bucketDurationSeconds : 0
    }))
    .sort((left, right) => left.startTime.localeCompare(right.startTime));
};

const aggregateTimeSeries = (series: FlowTimeBucket[][]): FlowTimeBucket[] => {
  const buckets = new Map<string, FlowTimeBucket>();
  for (const items of series) {
    for (const item of items) {
      const bucket = buckets.get(item.startTime) ?? {
        startTime: item.startTime,
        endTime: item.endTime,
        bucketDurationSeconds: item.bucketDurationSeconds,
        bytes: 0,
        packets: 0,
        observedBytesPerSecond: 0,
        observedPacketsPerSecond: 0,
        recordCount: 0
      };
      bucket.bytes += item.bytes;
      bucket.packets += item.packets;
      bucket.recordCount += item.recordCount;
      buckets.set(item.startTime, bucket);
    }
  }
  return [...buckets.values()]
    .map((item) => ({
      ...item,
      observedBytesPerSecond: item.bucketDurationSeconds > 0 ? item.bytes / item.bucketDurationSeconds : 0,
      observedPacketsPerSecond: item.bucketDurationSeconds > 0 ? item.packets / item.bucketDurationSeconds : 0
    }))
    .sort((left, right) => left.startTime.localeCompare(right.startTime));
};

const overview = (url: URL) => {
  const selected = selectedRecords(url);
  const topLimit = queryLimit(url, 'topLimit', 10);
  const totalBytes = selected.reduce((total, record) => total + record.bytes, 0);
  const totalPackets = selected.reduce((total, record) => total + record.packets, 0);
  const metadata = mockMetadata(url, selected);
  const durationSeconds = Math.max(0, (Date.parse(metadata.effectiveEndTime) - Date.parse(metadata.effectiveStartTime)) / 1000);
  const siteId = url.searchParams.get('siteId');
  const siteRecords = siteId ? selected.filter((record) => mockSiteId(record) === siteId) : [];
  const siteBytes = siteRecords.reduce((sum, record) => sum + record.bytes, 0);
  const sitePackets = siteRecords.reduce((sum, record) => sum + record.packets, 0);
  const siteBreakdown = siteId ? [{
    siteId,
    bytes: siteBytes,
    packets: sitePackets,
    recordCount: siteRecords.length,
    observedBytesPerSecond: durationSeconds > 0 ? siteBytes / durationSeconds : 0,
    observedPacketsPerSecond: durationSeconds > 0 ? sitePackets / durationSeconds : 0,
    trafficSharePercent: totalBytes > 0 ? siteBytes / totalBytes * 100 : 0
  }] : [];
  return {
    totalBytes,
    totalPackets,
    observedBytesPerSecond: durationSeconds > 0 ? totalBytes / durationSeconds : 0,
    observedPacketsPerSecond: durationSeconds > 0 ? totalPackets / durationSeconds : 0,
    observedRecordCount: selected.length,
    siteBreakdown,
    observedDelay: delaySummary(selected),
    timeSeries: timeSeries(selected, metadata),
    topSources: metrics(selected, (record) => record.sourceAddress, durationSeconds).slice(0, topLimit),
    topDestinations: metrics(selected, (record) => record.destinationAddress, durationSeconds).slice(0, topLimit),
    topProtocols: metrics(selected, (record) => record.protocol ?? 'unknown', durationSeconds).slice(0, topLimit),
    topPorts: metrics(selected, (record) => String(record.destinationPort), durationSeconds).slice(0, topLimit),
    topVlans: metrics(selected, (record) => String(record.vlanId), durationSeconds).slice(0, topLimit),
    topSourceAsns: metrics(selected, (record) => String(record.sourceAs), durationSeconds).slice(0, topLimit),
    topDestinationAsns: metrics(selected, (record) => String(record.destinationAs), durationSeconds).slice(0, topLimit),
    topNextHops: metrics(selected, (record) => record.nextHop, durationSeconds).slice(0, topLimit),
    nextHopCoverage: nextHopCoverage(selected, durationSeconds),
    metadata
  };
};

const conversations = (url: URL) => {
  const directionalPathLimit = 20;
  const selected = selectedRecords(url);
  const metadata = mockMetadata(url, selected);
  const totalBytes = selected.reduce((total, record) => total + record.bytes, 0);
  const requestedMode = url.searchParams.get('mode');
  const mode: FlowConversation['mode'] = requestedMode === 'endpoint_pair' ? 'endpoint_pair' : requestedMode === 'bidirectional' ? 'bidirectional' : 'directional';
  type MockConversation = FlowConversation & { matchedRecords: MockFlowRecord[] };
  const items = new Map<string, MockConversation>();

  for (const record of selected) {
    let sourceAddress = record.sourceAddress;
    let destinationAddress = record.destinationAddress;
    let sourcePort = record.sourcePort;
    let destinationPort = record.destinationPort;
    let canonicalDirection = true;
    if (mode === 'bidirectional' && (destinationAddress + ':' + destinationPort) < (sourceAddress + ':' + sourcePort)) {
      [sourceAddress, destinationAddress] = [destinationAddress, sourceAddress];
      [sourcePort, destinationPort] = [destinationPort, sourcePort];
    }
    if (mode === 'endpoint_pair' && destinationAddress < sourceAddress) {
      [sourceAddress, destinationAddress] = [destinationAddress, sourceAddress];
      canonicalDirection = false;
    }
    const key = mode === 'endpoint_pair'
      ? [exporterKey(record), sourceAddress, destinationAddress].join('|')
      : [exporterKey(record), sourceAddress, sourcePort, destinationAddress, destinationPort, record.protocol].join('|');
    const item: MockConversation = items.get(key) ?? {
      exporter: exporterForRecord(record),
      mode,
      sourceAddress,
      destinationAddress,
      ...(mode === 'endpoint_pair' ? {} : { sourcePort, destinationPort, protocol: record.protocol }),
      bytes: 0,
      ...(mode === 'endpoint_pair' ? { directionalTraffic: { firstToSecondBytes: 0, firstToSecondPackets: 0, secondToFirstBytes: 0, secondToFirstPackets: 0 }, directionalPaths: [], directionalPathsTruncated: false } : {}),
      packets: 0,
      recordCount: 0,
      trafficSharePercent: 0,
      timeSeries: [],
      matchedRecords: []
    };
    item.bytes += record.bytes;
    if (mode === 'endpoint_pair') {
      if (canonicalDirection) {
        item.directionalTraffic!.firstToSecondBytes += record.bytes;
        item.directionalTraffic!.firstToSecondPackets += record.packets;
      } else {
        item.directionalTraffic!.secondToFirstBytes += record.bytes;
        item.directionalTraffic!.secondToFirstPackets += record.packets;
      }
      const direction = canonicalDirection ? 'first_to_second' : 'second_to_first';
      const existingPath = item.directionalPaths!.find((path) => path.direction === direction && path.ingressIfIndex === record.inIfIndex && path.egressIfIndex === record.outIfIndex);
      const path = existingPath ?? {
        direction,
        ingressIfIndex: record.inIfIndex,
        egressIfIndex: record.outIfIndex,
        ...(mockInterfaceName(record.inIfIndex) ? { ingressInterfaceName: mockInterfaceName(record.inIfIndex) } : {}),
        ...(mockInterfaceName(record.outIfIndex) ? { egressInterfaceName: mockInterfaceName(record.outIfIndex) } : {}),
        bytes: 0,
        packets: 0,
        recordCount: 0
      };
      path.bytes += record.bytes;
      path.packets += record.packets;
      path.recordCount += 1;
      if (!existingPath) item.directionalPaths!.push(path);
    }
    item.packets += record.packets;
    item.recordCount += 1;
    item.matchedRecords.push(record);
    items.set(key, item);
  }

  const ranked = rank([...items.values()], (item) => [item.sourceAddress, item.destinationAddress, item.protocol].join('|'), Number.MAX_SAFE_INTEGER)
    .map(({ matchedRecords, directionalPaths, ...item }) => {
      const rankedPaths = directionalPaths?.slice().sort((left, right) =>
        right.bytes - left.bytes || right.packets - left.packets ||
        (left.direction === right.direction ? 0 : left.direction === 'first_to_second' ? -1 : 1) ||
        (left.ingressIfIndex ?? -1) - (right.ingressIfIndex ?? -1) ||
        (left.egressIfIndex ?? -1) - (right.egressIfIndex ?? -1)
      );
      return {
        ...item,
        trafficSharePercent: totalBytes > 0 ? item.bytes / totalBytes * 100 : 0,
        timeSeries: timeSeries(matchedRecords, metadata),
        ...(rankedPaths == null ? {} : {
          directionalPaths: rankedPaths.slice(0, directionalPathLimit),
          directionalPathsTruncated: rankedPaths.length > directionalPathLimit
        })
      };
    });

  const responsePage = topTenPage(url, ranked);

  return {
    ...responsePage,
    timeSeries: aggregateTimeSeries(responsePage.items.map((item) => item.timeSeries)),
    metadata
  };
};

const endpoints = (url: URL) => {
  const selected = selectedRecords(url);
  const metadata = mockMetadata(url, selected);
  const totalEndpointBytes = selected.reduce((total, record) => total + record.bytes * 2, 0);
  type MockEndpoint = FlowEndpoint & { matchedRecords: MockFlowRecord[] };
  const items = new Map<string, MockEndpoint>();
  const add = (address: string, record: MockFlowRecord, received: boolean) => {
    const item = items.get(address) ?? {
      address,
      sentBytes: 0,
      receivedBytes: 0,
      sentPackets: 0,
      receivedPackets: 0,
      ingressBytes: 0,
      egressBytes: 0,
      ingressPackets: 0,
      egressPackets: 0,
      bytes: 0,
      packets: 0,
      trafficSharePercent: 0,
      timeSeries: [],
      recordCount: 0,
      matchedRecords: []
    };
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
    item.matchedRecords.push(record);
    if (received) {
      item.ingressBytes += record.bytes;
      item.ingressPackets += record.packets;
      item.receivedBytes += record.bytes;
      item.receivedPackets += record.packets;
    } else {
      item.egressBytes += record.bytes;
      item.egressPackets += record.packets;
      item.sentBytes += record.bytes;
      item.sentPackets += record.packets;
    }
    items.set(address, item);
  };

  for (const record of selected) {
    add(record.sourceAddress, record, false);
    add(record.destinationAddress, record, true);
  }

  const ranked = rank([...items.values()], (item) => item.address, Number.MAX_SAFE_INTEGER)
    .map(({ matchedRecords, ...item }) => ({
      ...item,
      trafficSharePercent: totalEndpointBytes > 0 ? item.bytes / totalEndpointBytes * 100 : 0,
      timeSeries: timeSeries(matchedRecords, metadata)
    }));
  const responsePage = topTenPage(url, ranked);
  return {
    ...responsePage,
    timeSeries: aggregateTimeSeries(responsePage.items.map((item) => item.timeSeries)),
    metadata
  };
};

const ephemeralPortStart = 49152;

// Mock-only subset of the IANA Service Name and Transport Protocol Port Number
// Registry. Production responses receive the exact registry value from
// flow-service's bundled IANA snapshot; the UI does not derive these names.
const mockIANAServiceNames: Readonly<Record<string, Readonly<Record<number, string>>>> = {
  TCP: {
    53: 'domain',
    443: 'https',
    5001: 'commplex-link',
    5002: 'rfe',
    5003: 'fmpro-internal',
    5004: 'avt-profile-1',
    5005: 'avt-profile-2',
    5006: 'wsm-server',
    5432: 'postgresql',
    8080: 'http-alt',
    8443: 'pcsync-https',
    9443: 'tungsten-https'
  },
  UDP: {
    53: 'domain',
    443: 'https',
    5001: 'commplex-link',
    5002: 'rfe',
    5003: 'fmpro-internal',
    5004: 'avt-profile-1',
    5005: 'avt-profile-2',
    5006: 'wsm-server',
    5432: 'postgresql',
    8080: 'http-alt',
    8443: 'pcsync-https',
    9443: 'tungsten-https'
  }
};

const classifyService = (record: MockFlowRecord): { label: string; protocol: string; servicePort?: number; ianaServiceName?: string; toService: boolean } => {
  const protocol = (record.protocol ?? 'unknown').trim().toUpperCase() || 'UNKNOWN';
  const sourcePort = record.sourcePort;
  const destinationPort = record.destinationPort;
  const names = mockIANAServiceNames[protocol];
  let servicePort: number | undefined;
  if (sourcePort != null && destinationPort != null) {
    const sourceIsService = sourcePort < ephemeralPortStart;
    const destinationIsService = destinationPort < ephemeralPortStart;
    if (sourceIsService && !destinationIsService) servicePort = sourcePort;
    else if (destinationIsService && !sourceIsService) servicePort = destinationPort;
    else if (sourceIsService && destinationIsService) servicePort = Math.min(sourcePort, destinationPort);
  } else if (sourcePort != null && sourcePort < ephemeralPortStart) {
    servicePort = sourcePort;
  } else if (destinationPort != null && destinationPort < ephemeralPortStart) {
    servicePort = destinationPort;
  }
  if (servicePort == null) {
    return { label: sourcePort == null && destinationPort == null ? protocol : protocol + '/other', protocol, toService: true };
  }
  return {
    label: protocol + '/' + servicePort,
    protocol,
    servicePort,
    ...(names?.[servicePort] == null ? {} : { ianaServiceName: names[servicePort] }),
    toService: destinationPort === servicePort
  };
};

const services = (url: URL) => {
  const selected = selectedRecords(url);
  const metadata = mockMetadata(url, selected);
  const totalBytes = selected.reduce((total, record) => total + record.bytes, 0);
  type MockService = FlowService & { matchedRecords: MockFlowRecord[] };
  const items = new Map<string, MockService>();
  for (const record of selected) {
    const classification = classifyService(record);
    const item = items.get(classification.label) ?? {
      label: classification.label,
      protocol: classification.protocol,
      ...(classification.servicePort == null ? {} : { servicePort: classification.servicePort }),
      ...(classification.ianaServiceName == null ? {} : { ianaServiceName: classification.ianaServiceName }),
      toServiceBytes: 0,
      fromServiceBytes: 0,
      toServicePackets: 0,
      fromServicePackets: 0,
      bytes: 0,
      packets: 0,
      trafficSharePercent: 0,
      timeSeries: [],
      recordCount: 0,
      matchedRecords: []
    };
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
    item.matchedRecords.push(record);
    if (classification.toService) {
      item.toServiceBytes += record.bytes;
      item.toServicePackets += record.packets;
    } else {
      item.fromServiceBytes += record.bytes;
      item.fromServicePackets += record.packets;
    }
    items.set(classification.label, item);
  }
  const ranked = rank([...items.values()], (item) => item.label, Number.MAX_SAFE_INTEGER)
    .map(({ matchedRecords, ...item }) => ({
      ...item,
      trafficSharePercent: totalBytes > 0 ? item.bytes / totalBytes * 100 : 0,
      timeSeries: timeSeries(matchedRecords, metadata)
    }));
  const responsePage = topTenPage(url, ranked);
  return {
    ...responsePage,
    timeSeries: aggregateTimeSeries(responsePage.items.map((item) => item.timeSeries)),
    metadata
  };
};

const interfaceTotals = (url: URL) => {
  const selected = selectedRecords(url);
  const items = new Map<string, FlowInterfaceTotals>();
  const add = (ingress: boolean, ifIndex: number, record: MockFlowRecord) => {
    const key = exporterKey(record) + '|' + ifIndex;
    const item = items.get(key) ?? {
      exporter: exporterForRecord(record),
      ifIndex,
      ...(mockInterfaceName(ifIndex) == null ? {} : { interfaceName: mockInterfaceName(ifIndex) }),
      ingressBytes: 0,
      egressBytes: 0,
      totalBytes: 0,
      ingressPackets: 0,
      egressPackets: 0,
      totalPackets: 0,
      ingressRecordCount: 0,
      egressRecordCount: 0
    };
    if (ingress) {
      item.ingressBytes += record.bytes;
      item.ingressPackets += record.packets;
      item.ingressRecordCount += 1;
    } else {
      item.egressBytes += record.bytes;
      item.egressPackets += record.packets;
      item.egressRecordCount += 1;
    }
    item.totalBytes = item.ingressBytes + item.egressBytes;
    item.totalPackets = item.ingressPackets + item.egressPackets;
    items.set(key, item);
  };

  const direction = url.searchParams.get('direction');
  for (const record of selected) {
    if (direction == null || direction === 'input') add(true, record.inIfIndex, record);
    if (direction == null || direction === 'output') add(false, record.outIfIndex, record);
  }

  const ifIndex = numericQuery(url, 'ifIndex');
  const ranked = [...items.values()]
    .sort((left, right) => right.totalBytes - left.totalBytes || right.totalPackets - left.totalPackets || [left.exporter.flowType, left.exporter.samplerAddress, left.exporter.observationDomainId, left.ifIndex].join('|').localeCompare([right.exporter.flowType, right.exporter.samplerAddress, right.exporter.observationDomainId, right.ifIndex].join('|')))
    .filter((item) => ifIndex == null || item.ifIndex === ifIndex);

  return {
    ...page(url, ranked),
    metadata: mockMetadata(url, selected)
  };
};

const interfaces = (url: URL) => {
  const selected = selectedRecords(url);
  const items = new Map<string, FlowInterfaceTraffic>();
  const add = (direction: 'input' | 'output', ifIndex: number, record: MockFlowRecord) => {
    const key = exporterKey(record) + '|' + direction + '|' + ifIndex;
    const item = items.get(key) ?? {
      exporter: exporterForRecord(record),
      direction,
      ifIndex,
      ...(mockInterfaceName(ifIndex) == null ? {} : { interfaceName: mockInterfaceName(ifIndex) }),
      bytes: 0,
      packets: 0,
      recordCount: 0
    };
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
    items.set(key, item);
  };

  const direction = url.searchParams.get('direction');
  for (const record of selected) {
    if (direction == null || direction === 'input') add('input', record.inIfIndex, record);
    if (direction == null || direction === 'output') add('output', record.outIfIndex, record);
  }

  const ifIndex = numericQuery(url, 'ifIndex');
  const ranked = [...items.values()]
    .sort((left, right) => right.bytes - left.bytes || right.packets - left.packets || [left.exporter.flowType, left.exporter.samplerAddress, left.exporter.observationDomainId, left.direction, left.ifIndex].join('|').localeCompare([right.exporter.flowType, right.exporter.samplerAddress, right.exporter.observationDomainId, right.direction, right.ifIndex].join('|')))
    .filter((item) => ifIndex == null || item.ifIndex === ifIndex);

  return {
    ...page(url, ranked),
    metadata: mockMetadata(url, selected)
  };
};

const sources = (url: URL) => {
  const selected = selectedRecords(url);
  const items = new Map<string, FlowSourceSummary>();
  const bySource = new Map<string, MockFlowRecord[]>();
  for (const record of selected) {
    const sourceKey = '/' + record.flowType + '/' + record.samplerAddress + '/' + record.observationDomainId;
    const sourceRecords = bySource.get(sourceKey) ?? [];
    sourceRecords.push(record);
    bySource.set(sourceKey, sourceRecords);
    const item = items.get(sourceKey) ?? {
      sourceKey,
      flowType: record.flowType,
      samplerAddress: record.samplerAddress,
      observationDomainId: record.observationDomainId,
      lastSeenAt: record.flowEndTime,
      samplingState: record.samplingState,
      bytes: 0,
      packets: 0,
      recordCount: 0,
      observedBytesPerSecond: 0,
      observedPacketsPerSecond: 0,
      sequenceResetCandidate: false
    };
    item.bytes += record.bytes;
    item.packets += record.packets;
    item.recordCount += 1;
    if (record.flowEndTime > item.lastSeenAt) item.lastSeenAt = record.flowEndTime;
    items.set(sourceKey, item);
  }

  for (const [sourceKey, sourceRecords] of bySource) {
    const item = items.get(sourceKey);
    if (!item) continue;
    const ordered = [...sourceRecords].sort((left, right) => left.flowEndTime.localeCompare(right.flowEndTime));
    const metadata = mockMetadata(url, sourceRecords);
    const durationSeconds = Math.max(0, (Date.parse(metadata.effectiveEndTime) - Date.parse(metadata.effectiveStartTime)) / 1000);
    item.observedBytesPerSecond = durationSeconds > 0 ? item.bytes / durationSeconds : 0;
    item.observedPacketsPerSecond = durationSeconds > 0 ? item.packets / durationSeconds : 0;
    item.samplingState = aggregateSamplingState(sourceRecords);
    let gaps = 0;
    for (let index = 1; index < ordered.length; index += 1) {
      const previous = ordered[index - 1]!;
      const current = ordered[index]!;
      if (current.flowSequence < previous.flowSequence) item.sequenceResetCandidate = true;
      if (current.flowSequence > previous.flowSequence + 1) gaps += current.flowSequence - previous.flowSequence - 1;
    }
    if (gaps > 0) item.candidateSequenceGap = gaps;
  }

  return {
    ...page(url, rank([...items.values()], (item) => item.sourceKey, Number.MAX_SAFE_INTEGER)),
    metadata: mockMetadata(url, selected)
  };
};

const search = (url: URL) => {
  const selected = selectedRecords(url);
  return {
    ...page(url, selected),
    metadata: mockMetadata(url, selected)
  };
};

export const flowHandlers = [
  async ({ request, url }: MockContext) => {
    const orgId = orgIdFromPath(url.pathname);
    if (request.method === 'POST' && url.pathname.endsWith('/flows/reports')) {
      if (!orgId) return invalidRequest('an organization id is required');
      const payload = await request.json() as Record<string, unknown>;
      const report = createMockReport(orgId, payload);
      return report ? json(report, 202) : invalidRequest('a valid PDF report request is required');
    }

    if (request.method !== 'GET') return null;

    const reportId = flowReportId(url.pathname);
    if (reportId && url.pathname.endsWith('/download')) {
      const report = mockReports.get(reportId);
      if (!report || report.orgId !== orgId) return json({ type: 'not-found', status: 404, detail: 'Report not found' }, 404);
      return new Response(mockPdf(report), {
        headers: {
          'Content-Type': 'application/pdf',
          'Content-Disposition': `attachment; filename="${report.fileName}"`,
          'X-Mock-Route': 'flows'
        }
      });
    }
    if (reportId) {
      const report = mockReports.get(reportId);
      return report && report.orgId === orgId ? json(report) : json({ type: 'not-found', status: 404, detail: 'Report not found' }, 404);
    }

    if (url.pathname.endsWith('/flows/overview')) return json(overview(url));
    if (url.pathname.endsWith('/flows/conversations')) {
      const detail = topTenRequestError(url);
      return detail ? invalidRequest(detail) : json(conversations(url));
    }
    if (url.pathname.endsWith('/flows/endpoints')) {
      const detail = topTenRequestError(url);
      return detail ? invalidRequest(detail) : json(endpoints(url));
    }
    if (url.pathname.endsWith('/flows/services')) {
      const detail = topTenRequestError(url);
      return detail ? invalidRequest(detail) : json(services(url));
    }
    if (url.pathname.endsWith('/flows/interface-totals')) return json(interfaceTotals(url));
    if (url.pathname.endsWith('/flows/interfaces')) return json(interfaces(url));
    if (url.pathname.endsWith('/flows/search')) return json(search(url));
    if (url.pathname.endsWith('/flow-sources')) return json(sources(url));

    return null;
  }
];
